import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { packagingApi } from 'api/packaging';
import { ViewEntityHeader } from 'components/entityViewer/ViewEntityHeader';
import { ViewEntityPage } from 'components/entityViewer/ViewEntityPage';
import { PackagingPurchaseOrderHeaderContent } from 'components/packaging/components/packagingPOV2/entityView/PackagingPurchaseOrderHeaderContent';
import { PackagingPurchaseOrderItemsCard } from 'components/packaging/components/packagingPOV2/entityView/PackagingPurchaseOrderItemsCard';
import { PackagingPurchaseOrderPrimaryInfoCard } from 'components/packaging/components/packagingPOV2/entityView/PackagingPurchaseOrderPrimaryInfoCard';
import { usePackagingPurchaseOrderItems } from 'components/packaging/components/packagingPOV2/entityView/PackagingPurchaseOrderQueries';
import { usePackagingPOActions } from 'components/packaging/components/packagingPOV2/hooks/usePackagingPOActions';
import { usePackagingPOBackHref } from 'components/packaging/components/packagingPOV2/usePackagingPOBackHref';
import {
    buildPackagingMapItemDiff,
    buildPackagingPrimaryUpdatePayload,
    getPackagingPoMapId,
    toIntOrZero,
    toNumber,
    validatePackagingPOEdit,
} from 'components/packaging/components/packagingPOV2/utils/packagingPOValidation';
import { ReasonCodeDialog } from 'components/procurement/components/purchaseOrdersV2/ReasonCodeDialog';
import { toMoment } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { Button } from 'shadcn/components/ui/Button';

const createNewItemId = () => `new-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function getItemEditInputValue(itemEditStateRef, rowId, field) {
    const entry = itemEditStateRef?.current?.inputs?.[rowId]?.[field];
    if (entry === undefined) return undefined;
    if (entry && typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'value')) {
        return entry.value;
    }
    return entry;
}

const buildPackagingPrimaryDraft = (purchaseOrder) => {
    const scheduledDelivery = toMoment(purchaseOrder?.scheduledDeliveryDate);
    return {
        scheduledDeliveryDate: scheduledDelivery ? scheduledDelivery.format('YYYY-MM-DD') : '',
        scheduledDeliveryTime: scheduledDelivery ? scheduledDelivery.format('HH:mm') : '',
        referenceNumber: purchaseOrder?.referenceNumber ?? '',
        vendorId: purchaseOrder?.vendorID ?? purchaseOrder?.vendorId ?? null,
        crossDockId: purchaseOrder?.shipToCrossDockID ?? '',
    };
};

export function ViewPackagingPOEntity() {
    const { purchaseOrderId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const backHref = usePackagingPOBackHref();

    const {
        data: purchaseOrderDetail,
        error,
        isError,
        isFetching,
        isLoading,
        refetch,
    } = usePackagingPurchaseOrderItems({
        id: purchaseOrderId,
        enabled: Boolean(purchaseOrderId),
    });

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [primaryDraft, setPrimaryDraft] = useState(null);
    const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
    const [itemEditsVersion, setItemEditsVersion] = useState(0);

    const [addedItems, setAddedItems] = useState([]);
    const [removedItemIds, setRemovedItemIds] = useState(() => new Set());
    const itemEditStateRef = useRef({ inputs: {}, selects: {} });
    const primaryDraftRef = useRef(null);
    primaryDraftRef.current = primaryDraft;

    const validation = useMemo(
        () =>
            validatePackagingPOEdit({
                primaryDraft,
                addedItems,
                entityItems: purchaseOrderDetail?.items ?? [],
                removedItemIds,
                itemEditStateRef,
                getItemEditInputValue,
            }),
        [primaryDraft, addedItems, purchaseOrderDetail?.items, removedItemIds, itemEditsVersion],
    );

    const handleItemEditsChange = useCallback(() => {
        setItemEditsVersion((v) => v + 1);
    }, []);
    const [selectedPackagingOption, setSelectedPackagingOption] = useState(null);

    const canAddItem = Boolean(selectedPackagingOption);

    const handleRemoveNewRow = useCallback((rowId) => {
        setAddedItems((prev) => prev.filter((item) => String(item.id) !== rowId));
        if (itemEditStateRef?.current?.inputs) delete itemEditStateRef.current.inputs[rowId];
        if (itemEditStateRef?.current?.selects) delete itemEditStateRef.current.selects[rowId];
    }, []);

    const entityPurchaseOrder = purchaseOrderDetail ?? null;
    const sourcePurchaseOrderId = entityPurchaseOrder?.id ?? purchaseOrderId;
    const purchaseOrdersFilters =
        (typeof location.state?.packagingPurchaseOrdersFilters === 'string' &&
            location.state.packagingPurchaseOrdersFilters) ||
        (typeof location.state?.purchaseOrdersFilters === 'string' && location.state.purchaseOrdersFilters) ||
        '';

    const { cancelDialog, cancelState, confirmCancel, downloadPdf, downloadShipList, duplicate, sendToVendor } =
        usePackagingPOActions({
            getFiltersForBackNav: () => purchaseOrdersFilters,
            history,
            refetch,
        });

    const purchaseOrderItems = useMemo(() => {
        if (Array.isArray(entityPurchaseOrder?.items)) {
            return entityPurchaseOrder.items;
        }
        return [];
    }, [entityPurchaseOrder]);
    const { totalCases, totalPallets, totalUnits } = useMemo(() => {
        let casesTotal = 0;
        let unitsTotal = 0;
        let palletsTotal = 0;
        purchaseOrderItems.forEach((item) => {
            const qty = Number(item?.qty);
            if (Number.isFinite(qty)) {
                casesTotal += qty;
            }
            const unitsPerCase = Number(item?.unitsPerCase);
            if (Number.isFinite(qty) && Number.isFinite(unitsPerCase)) {
                unitsTotal += qty * unitsPerCase;
            }
            const numPallets = Number(item?.numPallets);
            if (Number.isFinite(numPallets)) {
                palletsTotal += numPallets;
            }
        });
        return { totalCases: casesTotal, totalPallets: palletsTotal, totalUnits: unitsTotal };
    }, [purchaseOrderItems]);

    const handleSelectedPackagingChange = useCallback((option) => {
        setSelectedPackagingOption(option ?? null);
    }, []);

    const handleAddItem = useCallback(() => {
        const model = selectedPackagingOption?.model;
        if (!model) return;

        const newItem = {
            id: createNewItemId(),
            packagingModelId: Number(selectedPackagingOption.value),
            masterSku: model.masterSKU ?? '',
            description: model.description ?? '',
            unitsPerCase: String(model.unitsPerCase ?? ''),
            unitsPerCaseUOM: 'ct',
            numPallets: '',
            qty: '',
            unitPrice: '',
            vendorSku: '',
            notes: '',
        };
        setAddedItems((prev) => [...prev, newItem]);
        setSelectedPackagingOption(null);
    }, [selectedPackagingOption]);

    const handleEditPurchaseOrder = useCallback(() => {
        if (!entityPurchaseOrder) return;
        setIsEditing(true);
        setHasAttemptedSave(false);
        setPrimaryDraft(buildPackagingPrimaryDraft(entityPurchaseOrder));
    }, [entityPurchaseOrder]);

    const handleCancelEditPurchaseOrder = useCallback(() => {
        setIsEditing(false);
        setPrimaryDraft(null);
        setHasAttemptedSave(false);
        setAddedItems([]);
        setRemovedItemIds(new Set());
        itemEditStateRef.current = { inputs: {}, selects: {} };
        setSelectedPackagingOption(null);
    }, []);

    const handleSavePurchaseOrder = useCallback(async () => {
        if (!entityPurchaseOrder) return;

        setHasAttemptedSave(true);
        if (!validation.isValid) return;

        const newItemsToAdd = (addedItems ?? []).filter((item) => !removedItemIds.has(String(item?.id)));

        const draft = primaryDraftRef.current ?? primaryDraft;
        if (!draft) return;

        const updatePayload = buildPackagingPrimaryUpdatePayload(draft);
        setIsSaving(true);
        const reportPartialSaveFailure = async (baseMessage, serverMsg) => {
            const message = serverMsg ? `${baseMessage} ${serverMsg}` : baseMessage;
            toast.error(message);
            await refetch();
        };
        try {
            const response = await packagingApi.updatePurchaseOrder(
                entityPurchaseOrder.id ?? purchaseOrderId,
                updatePayload,
            );

            if (!response?.status) {
                const message = response?.msg
                    ? `Failed to save purchase order details. ${response.msg}`
                    : 'Failed to save purchase order details.';
                toast.error(message);
                return;
            }

            const poID = entityPurchaseOrder.id ?? purchaseOrderId;

            const persistedItems = (entityPurchaseOrder.items ?? []).filter((item) => {
                const id = getPackagingPoMapId(item);
                if (!id || String(id).startsWith('new-')) return false;
                if (removedItemIds.has(id)) return false;
                return true;
            });
            const persistedItemUpdateEntries = persistedItems
                .map((item) => {
                    const rowId = getPackagingPoMapId(item);
                    const uiDraft = {
                        qty: getItemEditInputValue(itemEditStateRef, rowId, 'qty') ?? item.qty,
                        unitPrice: getItemEditInputValue(itemEditStateRef, rowId, 'unitPrice') ?? item.unitPrice,
                        numPallets: getItemEditInputValue(itemEditStateRef, rowId, 'numPallets') ?? item.numPallets,
                        unitsPerCase:
                            getItemEditInputValue(itemEditStateRef, rowId, 'unitsPerCase') ?? item.unitsPerCase,
                        vendorSku: getItemEditInputValue(itemEditStateRef, rowId, 'vendorSku') ?? item.vendorSku,
                        notes: getItemEditInputValue(itemEditStateRef, rowId, 'notes') ?? item.notes ?? '',
                    };
                    const diff = buildPackagingMapItemDiff({ item, uiDraft });
                    return Object.keys(diff).length > 0 ? { poMapID: rowId, updates: diff } : null;
                })
                .filter(Boolean);
            if (persistedItemUpdateEntries.length > 0) {
                const updateResults = await Promise.all(
                    persistedItemUpdateEntries.map(({ poMapID, updates: payload }) =>
                        packagingApi.updatePurchaseOrderMap(poID, poMapID, payload),
                    ),
                );
                const failedUpdate = updateResults.find((r) => r?.status === false);
                if (failedUpdate) {
                    await reportPartialSaveFailure(
                        'Purchase order details were saved, but updating some items failed. The page has been refreshed to show the current state.',
                        failedUpdate?.msg,
                    );
                    return;
                }
            }

            const existingIdsToRemove = [...removedItemIds]
                .map((id) => String(id))
                .filter((idStr) => !idStr.startsWith('new-'));
            const removeResults = await Promise.all(
                existingIdsToRemove.map((idStr) => packagingApi.removeItemFromPO(poID, idStr)),
            );
            const failedRemove = removeResults.find((r) => r?.status === false);
            if (failedRemove) {
                await reportPartialSaveFailure(
                    'Purchase order details were saved, but removing some items failed. The page has been refreshed to show the current state.',
                    failedRemove?.msg,
                );
                return;
            }

            if (newItemsToAdd.length > 0) {
                const addPayload = {
                    items: newItemsToAdd.map((item) => {
                        const rowId = String(item.id);
                        const qtyRaw = getItemEditInputValue(itemEditStateRef, rowId, 'qty') ?? item.qty;
                        const unitPriceRaw =
                            getItemEditInputValue(itemEditStateRef, rowId, 'unitPrice') ?? item.unitPrice;
                        const unitsPerCaseRaw =
                            getItemEditInputValue(itemEditStateRef, rowId, 'unitsPerCase') ?? item.unitsPerCase;
                        const vendorSku = getItemEditInputValue(itemEditStateRef, rowId, 'vendorSku') ?? item.vendorSku;
                        const numPalletsRaw =
                            getItemEditInputValue(itemEditStateRef, rowId, 'numPallets') ?? item.numPallets;
                        const notes = getItemEditInputValue(itemEditStateRef, rowId, 'notes') ?? item.notes ?? '';

                        const unitPriceNum = toNumber(unitPriceRaw);
                        const unitPrice = Number.isFinite(unitPriceNum) ? unitPriceNum : unitPriceRaw;

                        return {
                            id: item.packagingModelId,
                            qty: toIntOrZero(qtyRaw),
                            unitPrice,
                            unitsPerCase: toIntOrZero(unitsPerCaseRaw),
                            unitsPerCaseUOM: 'ct',
                            vendorSku,
                            numPallets: toIntOrZero(numPalletsRaw),
                            notes,
                        };
                    }),
                };
                const addResponse = await packagingApi.addItemToPurchaseOrder(poID, addPayload);
                if (addResponse?.status === false) {
                    await reportPartialSaveFailure(
                        'Purchase order details were saved, but adding items failed. The page has been refreshed to show the current state.',
                        addResponse?.msg,
                    );
                    return;
                }
            }

            toast.success('Purchase order updated.');
            setIsEditing(false);
            setPrimaryDraft(null);
            setHasAttemptedSave(false);
            setAddedItems([]);
            setRemovedItemIds(new Set());
            itemEditStateRef.current = { inputs: {}, selects: {} };
            setSelectedPackagingOption(null);
            await refetch();
        } catch (err) {
            toast.error(`Failed to save purchase order. ${err?.message ?? ''}`.trim());
        } finally {
            setIsSaving(false);
        }
    }, [addedItems, entityPurchaseOrder, purchaseOrderId, refetch, removedItemIds, validation.isValid]);

    if (isLoading || isFetching) {
        return (
            <div className="flex min-h-[400px] items-center justify-center rounded-md border border-border bg-card px-4 py-12 text-center">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Loading purchase order…</span>
                </div>
            </div>
        );
    }

    if (isError) {
        const errorMessage = error?.message || 'Failed to load purchase order';
        return (
            <div className="flex min-h-[400px] items-center justify-center rounded-md border border-border bg-card px-4 py-12 text-center">
                <div className="flex flex-col items-center gap-4 max-w-md">
                    <AlertCircle className="h-8 w-8 text-destructive" />
                    <div className="flex flex-col items-center gap-2">
                        <span className="text-sm font-medium text-foreground">Unable to load purchase order</span>
                        <span className="text-sm text-muted-foreground">{errorMessage}</span>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
                        <Button variant="outline" onClick={() => refetch()}>
                            Retry
                        </Button>
                        <Button variant="outline" onClick={() => history.push(backHref)}>
                            Back to POs
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const header = (
        <ViewEntityHeader
            backHref={backHref}
            backLabel="Back to POs"
            entityIdentificationComponent={
                <PackagingPurchaseOrderHeaderContent
                    purchaseOrder={entityPurchaseOrder}
                    purchaseOrderId={purchaseOrderId}
                    onEdit={isEditing ? undefined : handleEditPurchaseOrder}
                    isEditing={isEditing}
                    isSaving={isSaving}
                    onCancel={handleCancelEditPurchaseOrder}
                    onSave={handleSavePurchaseOrder}
                    hasValidationErrors={hasAttemptedSave && !validation.isValid}
                    onCancelPurchaseOrder={() =>
                        cancelDialog.open(entityPurchaseOrder ?? { id: sourcePurchaseOrderId })
                    }
                    onDuplicate={() => duplicate(entityPurchaseOrder ?? sourcePurchaseOrderId)}
                    onDownloadPdf={() => downloadPdf(entityPurchaseOrder)}
                    onDownloadShipList={() => downloadShipList(entityPurchaseOrder)}
                    onSendToVendor={() => sendToVendor(entityPurchaseOrder)}
                />
            }
        />
    );

    const primaryInformationCard = (
        <PackagingPurchaseOrderPrimaryInfoCard
            purchaseOrder={entityPurchaseOrder}
            isEditing={isEditing}
            draft={primaryDraft}
            onDraftChange={setPrimaryDraft}
            primaryErrors={validation.primaryErrors}
            primaryFieldErrors={validation.primaryFieldErrors}
            showValidation={hasAttemptedSave}
            totalCases={totalCases}
            totalUnits={totalUnits}
            totalPallets={totalPallets}
        />
    );
    const itemInformationCard = (
        <PackagingPurchaseOrderItemsCard
            addedItems={addedItems}
            canAddItem={canAddItem}
            isEditing={isEditing}
            itemErrors={validation.itemErrors}
            itemEditStateRef={itemEditStateRef}
            onAddItem={handleAddItem}
            onItemEditsChange={handleItemEditsChange}
            onRemoveNewRow={handleRemoveNewRow}
            onSelectedPackagingChange={handleSelectedPackagingChange}
            packagingOptionsEnabled={isEditing}
            purchaseOrder={entityPurchaseOrder}
            removedItemIds={removedItemIds}
            selectedPackagingOption={selectedPackagingOption}
            setRemovedItemIds={setRemovedItemIds}
            showValidation={hasAttemptedSave}
        />
    );

    return (
        <>
            <ViewEntityPage
                header={header}
                primaryInformationCard={primaryInformationCard}
                additionalInformationCards={itemInformationCard}
            />
            <ReasonCodeDialog
                open={cancelDialog.isOpen}
                title="Select A Reason Code and Confirm"
                onCancel={cancelDialog.close}
                onConfirm={confirmCancel}
                isSubmitting={cancelState.isSubmitting}
            />
        </>
    );
}

ViewPackagingPOEntity.displayName = 'ViewPackagingPOEntity';
